import moment, { Moment } from 'moment'
import { useSetState } from 'ahooks'
import {
  Affix,
  Button,
  Card,
  DateRangePicker,
  Error as ErrorComponent,
  ErrorAlert,
  Form,
  Info,
  Radio,
  SEO,
  Space,
  Title,
} from 'components/common'
import Layout from 'components/layouts/tow-column'
import {
  ReportErrorCode,
  useScheduleExportMutation,
} from 'lib/graphql/storeadmin'
import { downloadFile } from 'lib/common'

type FormValues = {
  dateRange: Moment[]
  target: 'all' | 'individual'
  approvalOnly: boolean
}

type ErrorAlertState = {
  visible: boolean
  messages: string[]
}

export const Head = () => <SEO title="利用予定表" />

const IndexPage = () => {
  const [form] = Form.useForm()
  const [mutate, { error, loading }] = useScheduleExportMutation({
    errorPolicy: 'all',
  })
  // Schedule error useState
  const [state, setState] = useSetState<ErrorAlertState>({
    visible: false,
    messages: [],
  })

  const onFinish = async (values: FormValues) => {
    const { dateRange, approvalOnly } = values
    const dateFormat = 'YYYY-MM-DD'
    try {
      const { data } = await mutate({
        variables: {
          start: dateRange[0].format(dateFormat),
          end: dateRange[1].format(dateFormat),
          approvalOnly,
        },
      })

      const userErrors = data.reportScheduleExport.userErrors
      if (userErrors.length > 0) {
        const errorMessages = userErrors.map(({ code }) => {
          switch (code) {
            case ReportErrorCode.RangeInvalid:
              return '集計期間が１年を超えています。'
            case ReportErrorCode.NotFound:
              return '出力可能なデータが存在しませんでした。'
            default:
              return '利用予定表出力時に予期せぬエラーが発生しました。'
          }
        })
        setState({ visible: true, messages: errorMessages })
      } else {
        setState({ visible: false, messages: [] })
        const url = data.reportScheduleExport.url
        downloadFile(url)
      }
    } catch (e) {
      setState({
        visible: true,
        messages: ['サーバーで予期せぬエラーが発生しました。。'],
      })
    }
  }

  const onCloseAlert = () => {
    setState({ visible: false })
  }

  if (error) {
    return ErrorComponent.render(error)
  }

  return (
    <>
      <Form
        form={form}
        component="form"
        requiredMark="optional"
        initialValues={{
          dateRange: [moment(), moment()],
          target: null,
          approvalOnly: null,
        }}
        onFinish={onFinish}
      >
        <Layout title="利用予定表">
          <Layout.Section size="large">
            {state.visible && (
              <ErrorAlert
                onCloseAlert={onCloseAlert}
                alertDescription={state.messages}
              />
            )}
            <Layout.Item>
              <Card shadow>
                <Form.Item
                  name="dateRange"
                  label={
                    <Info
                      title={
                        '開始月刻は終了刻月より前である必要があります。例：2022-01 〜 22-02 '
                      }
                    >
                      <Title level={5}>集計期間</Title>
                    </Info>
                  }
                  rules={[
                    { required: true, message: '集計期間は必須です' },
                    () => ({
                      validator(_, value) {
                        // 1 月から 12 月を選択された場合差は 11 になる
                        if (value[1].diff(value[0], 'months') > 11) {
                          return Promise.reject(
                            new Error('集計期間は最大1年です')
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                  required
                >
                  <DateRangePicker picker="month" />
                </Form.Item>
              </Card>
            </Layout.Item>

            {/* TODO: 集計対象の選択は Phase 2 以降
            <Layout.Item>
              <Card shadow>
                <Form.Item
                  label={<Title level={5}>集計対象</Title>}
                  name="target"
                  rules={[{ required: true, message: '集計対象は必須です' }]}
                  required
                >
                  <Radio.Group>
                    <Space direction="vertical">
                      <Radio value="all">全施設</Radio>
                      <Radio value="individual">施設単位</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Layout.Item>
            */}

            <Layout.Item>
              <Card shadow>
                <Form.Item
                  name="approvalOnly"
                  label={<Title level={5}>掲載内容</Title>}
                  rules={[{ required: true, message: '掲載内容は必須です' }]}
                  required
                >
                  <Radio.Group>
                    <Space direction="vertical">
                      <Radio value={false}>
                        <Info title="すべての予約情報が出力されます。">
                          管理者用
                        </Info>
                      </Radio>
                      <Radio value={true}>
                        <Info title="利用予定表への掲載が許可されたもののみ出力されます。">
                          一般用
                        </Info>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Layout.Item>
          </Layout.Section>

          <Layout.Section size="small">
            <Affix offsetTop={120}>
              <Card shadow>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  利用予定表を出力する
                </Button>
              </Card>
            </Affix>
          </Layout.Section>
        </Layout>
      </Form>
    </>
  )
}

export default IndexPage
